import { jsx } from "react/jsx-runtime";
import { cva } from "@/styled-system/css";
import { Box, Text } from "@zillow/constellation-10";
import PropTypes from "prop-types";
import "react";
import { resolveSpacing } from "../../utils/token-migration/migrate-responsive.esm.js";
const figureRecipe = cva({
  base: {
    // By default <figure> has browser margins - reset them
    margin: 0,
    lineHeight: 0
  },
  variants: {
    marginBlockStart: {
      none: {
        marginBlockStart: "none"
      },
      tightest: {
        marginBlockStart: "tightest"
      },
      tighter: {
        marginBlockStart: "tighter"
      },
      tight: {
        marginBlockStart: "tight"
      },
      default: {
        marginBlockStart: "default"
      },
      loose: {
        marginBlockStart: "loose"
      },
      looser: {
        marginBlockStart: "looser"
      },
      "layout.tightest": {
        marginBlockStart: "layout.tightest"
      },
      "layout.tighter": {
        marginBlockStart: "layout.tighter"
      },
      "layout.tight": {
        marginBlockStart: "layout.tight"
      },
      "layout.default": {
        marginBlockStart: "layout.default"
      },
      "layout.loose": {
        marginBlockStart: "layout.loose"
      },
      "layout.looser": {
        marginBlockStart: "layout.looser"
      },
      "layout.loosest": {
        marginBlockStart: "layout.loosest"
      },
      "layout.vast.default": {
        marginBlockStart: "layout.vast.default"
      },
      "layout.vast.loose": {
        marginBlockStart: "layout.vast.loose"
      }
    },
    marginBlockEnd: {
      none: {
        marginBlockEnd: "none"
      },
      tightest: {
        marginBlockEnd: "tightest"
      },
      tighter: {
        marginBlockEnd: "tighter"
      },
      tight: {
        marginBlockEnd: "tight"
      },
      default: {
        marginBlockEnd: "default"
      },
      loose: {
        marginBlockEnd: "loose"
      },
      looser: {
        marginBlockEnd: "looser"
      },
      "layout.tightest": {
        marginBlockEnd: "layout.tightest"
      },
      "layout.tighter": {
        marginBlockEnd: "layout.tighter"
      },
      "layout.tight": {
        marginBlockEnd: "layout.tight"
      },
      "layout.default": {
        marginBlockEnd: "layout.default"
      },
      "layout.loose": {
        marginBlockEnd: "layout.loose"
      },
      "layout.looser": {
        marginBlockEnd: "layout.looser"
      },
      "layout.loosest": {
        marginBlockEnd: "layout.loosest"
      },
      "layout.vast.default": {
        marginBlockEnd: "layout.vast.default"
      },
      "layout.vast.loose": {
        marginBlockEnd: "layout.vast.loose"
      }
    },
    marginInlineStart: {
      none: {
        marginInlineStart: "none"
      },
      tightest: {
        marginInlineStart: "tightest"
      },
      tighter: {
        marginInlineStart: "tighter"
      },
      tight: {
        marginInlineStart: "tight"
      },
      default: {
        marginInlineStart: "default"
      },
      loose: {
        marginInlineStart: "loose"
      },
      looser: {
        marginInlineStart: "looser"
      },
      "layout.tightest": {
        marginInlineStart: "layout.tightest"
      },
      "layout.tighter": {
        marginInlineStart: "layout.tighter"
      },
      "layout.tight": {
        marginInlineStart: "layout.tight"
      },
      "layout.default": {
        marginInlineStart: "layout.default"
      },
      "layout.loose": {
        marginInlineStart: "layout.loose"
      },
      "layout.looser": {
        marginInlineStart: "layout.looser"
      },
      "layout.loosest": {
        marginInlineStart: "layout.loosest"
      },
      "layout.vast.default": {
        marginInlineStart: "layout.vast.default"
      },
      "layout.vast.loose": {
        marginInlineStart: "layout.vast.loose"
      }
    },
    marginInlineEnd: {
      none: {
        marginInlineEnd: "none"
      },
      tightest: {
        marginInlineEnd: "tightest"
      },
      tighter: {
        marginInlineEnd: "tighter"
      },
      tight: {
        marginInlineEnd: "tight"
      },
      default: {
        marginInlineEnd: "default"
      },
      loose: {
        marginInlineEnd: "loose"
      },
      looser: {
        marginInlineEnd: "looser"
      },
      "layout.tightest": {
        marginInlineEnd: "layout.tightest"
      },
      "layout.tighter": {
        marginInlineEnd: "layout.tighter"
      },
      "layout.tight": {
        marginInlineEnd: "layout.tight"
      },
      "layout.default": {
        marginInlineEnd: "layout.default"
      },
      "layout.loose": {
        marginInlineEnd: "layout.loose"
      },
      "layout.looser": {
        marginInlineEnd: "layout.looser"
      },
      "layout.loosest": {
        marginInlineEnd: "layout.loosest"
      },
      "layout.vast.default": {
        marginInlineEnd: "layout.vast.default"
      },
      "layout.vast.loose": {
        marginInlineEnd: "layout.vast.loose"
      }
    }
  }
});
function collectSpacing({
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  margin
}) {
  const result = {
    marginTop,
    marginBottom,
    marginLeft,
    marginRight
  };
  if (margin && typeof margin === "object") {
    Object.entries(margin).forEach(([key, value]) => {
      if (value && !result[key]) {
        result[key] = value;
      }
    });
  }
  return result;
}
function buildMarginProps(spacing) {
  const recipeProps = {};
  const responsiveCss = {};
  const mappings = [["marginTop", "marginBlockStart"], ["marginBottom", "marginBlockEnd"], ["marginLeft", "marginInlineStart"], ["marginRight", "marginInlineEnd"]];
  mappings.forEach(([v8Key, v10Key]) => {
    const resolved = resolveSpacing(spacing[v8Key]);
    if (typeof resolved === "string") {
      recipeProps[v10Key] = resolved;
    } else if (resolved) {
      responsiveCss[v10Key] = resolved;
    }
  });
  return {
    recipeProps,
    responsiveCss
  };
}
const Figure = ({
  margin,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  children,
  ...rest
}) => {
  const spacing = collectSpacing({
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    margin
  });
  const {
    recipeProps,
    responsiveCss
  } = buildMarginProps(spacing);
  return /* @__PURE__ */ jsx(Box, { asChild: true, css: {
    ...figureRecipe.raw(recipeProps),
    ...responsiveCss
  }, ...rest, children: /* @__PURE__ */ jsx("figure", { children }) });
};
Figure.propTypes = {
  children: PropTypes.node,
  margin: PropTypes.object,
  marginTop: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  marginBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  marginLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  marginRight: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};
const FigureCaption = ({
  children,
  textAlign = "center",
  ...rest
}) => {
  return /* @__PURE__ */ jsx(Text, { asChild: true, textStyle: "body-sm", css: {
    color: "text.subtle",
    // coolGray500 → text.subtle (#535364)
    marginBlockStart: "tighter",
    // xs → tighter (8px)
    marginBlockEnd: "none",
    textAlign
  }, ...rest, children: /* @__PURE__ */ jsx("figcaption", { children }) });
};
FigureCaption.propTypes = {
  children: PropTypes.node,
  textAlign: PropTypes.oneOf(["left", "center", "right", "inherit"])
};
Figure.Caption = FigureCaption;
export {
  Figure as default
};
